import { MINUTA_TEMPLATE } from "../constants/minutaTemplate";

/**
 * A lightweight markdown parser for formatting the AI-generated summary.
 */
export function parseMarkdown(md: string): string {
  if (!md) return "";
  const lines = md.split("\n");
  let inList = false;
  const htmlLines: string[] = [];

  const getSectionSvg = (title: string): string => {
    const t = title.toLowerCase();
    if (/acuerdo/i.test(t)) {
      return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" style="flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>';
    }
    if (/tema|discutido|punto/i.test(t)) {
      return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>';
    }
    if (/próximo|proximo|paso|acción|accion|seguir/i.test(t)) {
      return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" style="flex-shrink:0"><polyline points="9 18 15 12 9 6"/></svg>';
    }
    if (/cita|destacada|frase/i.test(t)) {
      return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2" stroke-linecap="round" style="flex-shrink:0"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';
    }
    if (/minuta|resumen/i.test(t)) {
      return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" style="flex-shrink:0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
    }
    return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith("# ") || trimmed.startsWith("## ") || trimmed.startsWith("### ") || trimmed.startsWith("#### ")) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      const titleText = trimmed.replace(/^#+\s*/, "");
      const isMainHeading = trimmed.startsWith("# ") || (trimmed.startsWith("## ") && /minuta|resumen/i.test(titleText));

      if (isMainHeading) {
        htmlLines.push(
          `<h2 style="font-size: 15px; font-weight:700; color:var(--t1); margin: 6px 0 12px;">${titleText}</h2>`
        );
      } else {
        htmlLines.push(
          `<h3 style="font-size: 13px; font-weight:700; color:var(--accent); margin: 16px 0 6px;">${titleText}</h3>`
        );
      }
    } else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (!inList) {
        htmlLines.push('<ul style="margin-bottom: 12px; padding-left: 18px; text-align: left;">');
        inList = true;
      }
      let content = trimmed.substring(2);
      content = content.replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="color:var(--t1); font-weight:600;">$1</strong>'
      );
      content = content.replace(
        /\*(.*?)\*/g,
        '<em style="color:var(--t1); font-style: italic;">$1</em>'
      );
      htmlLines.push(
        `<li style="margin-bottom: 4px; color:var(--t2); line-height: 1.6; list-style-type: disc;">${content}</li>`
      );
    } else if (/^\d+\.\s+/.test(trimmed)) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      let content = trimmed.replace(/^\d+\.\s+/, "");
      content = content.replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="color:var(--t1); font-weight:600;">$1</strong>'
      );
      content = content.replace(
        /\*(.*?)\*/g,
        '<em style="color:var(--t1); font-style: italic;">$1</em>'
      );
      const matchNum = trimmed.match(/^\d+/);
      const numStr = matchNum ? matchNum[0] : "1";
      htmlLines.push(
        `<div style="margin-bottom: 6px; color:var(--t2); line-height: 1.6; padding-left: 6px; text-align: left;"><span style="color:var(--accent); font-weight:600; margin-right:6px;">${numStr}.</span> ${content}</div>`
      );
    } else if (trimmed.startsWith("> ") || (trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("_") && trimmed.endsWith("_") && trimmed.length > 2)) {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      let content = trimmed.replace(/^>\s*/, "").replace(/^_|_$/g, "");
      htmlLines.push(
        `<em style="color:var(--t1); font-style:normal; border-left:2px solid var(--accent); padding-left:10px; display:block; margin:8px 0; line-height:1.6;">${content}</em>`
      );
    } else if (trimmed === "") {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      htmlLines.push('<div style="height: 6px;"></div>');
    } else {
      if (inList) {
        htmlLines.push("</ul>");
        inList = false;
      }
      let content = trimmed;
      content = content.replace(
        /\*\*(.*?)\*\*/g,
        '<strong style="color:var(--t1); font-weight:600;">$1</strong>'
      );
      content = content.replace(
        /\*(.*?)\*/g,
        '<em style="color:var(--t1); font-style: italic;">$1</em>'
      );
      htmlLines.push(
        `<p style="margin-bottom: 8px; color:var(--t2); line-height: 1.6; text-align: left;">${content}</p>`
      );
    }
  }

  if (inList) {
    htmlLines.push("</ul>");
  }
  return htmlLines.join("\n");
}

/**
 * Download the meeting summary in Markdown format.
 */
export function downloadMarkdown(summaryText: string): void {
  if (!summaryText) return;
  const blob = new Blob([summaryText], { type: "text/markdown;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  const now = new Date();
  const dateStr = now.toISOString().split("T")[0];
  const timeStr = now.toLocaleTimeString([], { hour12: false }).replace(/:/g, "-");

  link.href = url;
  link.setAttribute("download", `Kira_Minuta_${dateStr}_${timeStr}.md`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download the meeting summary in HTML format (using the macOS styled template).
 */
export function downloadHtml(summaryText: string): void {
  if (!summaryText) return;

  const now = new Date();
  const dateStr = now.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString([], { hour12: false }).replace(/:/g, "-");
  const dateFileStr = now.toISOString().split("T")[0];

  const title = "Minuta de Reunión — Kira";
  const contentHtml = parseMarkdown(summaryText);

  const finalHtml = MINUTA_TEMPLATE
    .replace(/\{\{TITLE\}\}/g, title)
    .replace(/\{\{DATE\}\}/g, dateStr)
    .replace(/\{\{CONTENT\}\}/g, contentHtml);

  const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", `Kira_Minuta_${dateFileStr}_${timeStr}.html`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadChatTxt(transcript: any[]): void {
  if (!transcript || transcript.length === 0) return;
  const content = transcript.map(t => `[${t.timestamp}] ${t.orig} -> ${t.tran}`).join("\n");
  const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStr = new Date().toISOString().split("T")[0];
  
  link.href = url;
  link.setAttribute("download", `Kira_Chat_${dateStr}.txt`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadChatJson(transcript: any[]): void {
  if (!transcript || transcript.length === 0) return;
  const content = JSON.stringify(transcript, null, 2);
  const blob = new Blob([content], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStr = new Date().toISOString().split("T")[0];
  
  link.href = url;
  link.setAttribute("download", `Kira_Chat_${dateStr}.json`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadChatHtml(transcript: any[]): void {
  if (!transcript || transcript.length === 0) return;
  const listItems = transcript.map(t => 
    `<div style="margin-bottom: 12px; font-family: sans-serif;">
      <div style="font-size: 12px; color: #666;">[${t.timestamp}]</div>
      <div style="font-weight: bold; color: #333;">${t.orig}</div>
      <div style="font-style: italic; color: #007aff; margin-top: 4px;">${t.tran}</div>
    </div>`
  ).join("\n");
  const content = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Kira Chat Transcript</title></head><body style="padding: 20px; background: #fafafa;"><div style="max-width: 800px; margin: 0 auto; background: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">${listItems}</div></body></html>`;
  const blob = new Blob([content], { type: "text/html;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const dateStr = new Date().toISOString().split("T")[0];
  
  link.href = url;
  link.setAttribute("download", `Kira_Chat_${dateStr}.html`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
